import { PhoneNumberUtil } from "google-libphonenumber";
import * as Yup from "yup";
import { RequiredStringSchema } from "yup/lib/string";

export * from "./common";
export * from "./pricing";
export * from "./subscribers";
export * from "./campaignMetadata";
export * from "./campaignCreation";
export * from "./audienceTargeting";
export * from "./partialSendEvaluation";
export * from "./ownedPhoneNumber";

// Based on https://stackoverflow.com/a/106223 but requires at least 2
// segments, and the final TLD must be at least 2 characters
export const DOMAIN_NAME_REGEX =
  /^(([a-z0-9]|[a-z0-9][a-z0-9-]*[a-z0-9])\.){1,}([a-z0-9][a-z0-9-]*[a-z0-9])$/;

export function e164USPhoneNumber(yup: typeof Yup): Yup.StringSchema {
  return yup
    .string()
    .matches(/^\+1\d{10}$/, "Must be a valid E.164-formatted US phone number");
}

export function shortCodeOrE164PhoneNumber(yup: typeof Yup): Yup.StringSchema {
  return yup
    .string()
    .matches(
      /^(\d{5,6}|\+1\d{10})$/,
      "Must be a valid 5-digit or 6-digit short code or an E.164-formatted US phone number"
    );
}

const startTime = (): RequiredStringSchema<any, any> =>
  Yup.string().matches(
    /^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/,
    "Must be a valid time"
  );

const endTime = ({
  startTimeField = "textingHoursStart",
}: { startTimeField?: string } = {}): RequiredStringSchema<any, any> =>
  Yup.string()
    .matches(
      /^([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/,
      "Must be a valid time"
    )
    .test("is-greater", "Must be after start time", function (value) {
      const { [startTimeField]: startTime } = this.parent;
      return (
        !value ||
        !startTime ||
        new Date(`2020-01-01T${startTime}Z`) < new Date(`2020-01-01T${value}Z`)
      );
    });

const expirationMax = 24 * 60; // 24 hours in minutes

export function createOrEditSubaccountInputSchema(
  yup: typeof Yup,
  isEdit = false
): Yup.ObjectSchema<{
  input: Yup.ObjectSchema<{
    name: Yup.StringSchema;
    slug: Yup.StringSchema<string | null | undefined>;
    textingHoursTimezone: Yup.StringSchema;
    textingHoursStart: Yup.StringSchema;
    textingHoursEnd: Yup.StringSchema;
    texterAssignmentBatchSize: Yup.NumberSchema;
    texterAssignmentExpirationMinutes: Yup.NumberSchema;
  }>;
}> {
  const slugSchema: Yup.StringSchema = yup
    .string()
    .matches(
      /^[a-z0-9-]*$/,
      "Can only contain lowercase letters, digits, and dashes"
    );

  return yup.object({
    input: yup.object({
      name: yup
        .string()
        .min(3, "Too short")
        .required("Subaccount name is required"),
      slug: isEdit
        ? slugSchema.required("Slug is required")
        : slugSchema.nullable(),
      textingHoursTimezone: yup.string().required("Required"),
      textingHoursStart: startTime().required("Required"),
      textingHoursEnd: endTime().required("Required"),
      texterAssignmentBatchSize: yup
        .number()
        .required("Required")
        .typeError("Must be a number between 1 and 250")
        .min(1, "Must be at least 1")
        .max(250, "Must be at most 250"),
      texterAssignmentExpirationMinutes: yup
        .number()
        .required("Required")
        .min(5, "Must be at least 5")
        .max(expirationMax, "Must be at most 24 hours"),
    }),
  });
}

export function createOrEditResponseScriptGroupInputSchema(
  yup: typeof Yup
): Yup.ObjectSchema<{ input: Yup.ObjectSchema<{ title: Yup.StringSchema }> }> {
  return yup.object({
    input: yup.object({
      title: yup.string().required("Required"),
    }),
  });
}

export const MAX_KEYWORD_LENGTH = 15;

export function createOrEditResponseScriptInputSchema(
  yup: typeof Yup
): Yup.ObjectSchema<{
  input: Yup.ObjectSchema<{
    title: Yup.StringSchema;
    script: Yup.StringSchema;
    groupIds: Yup.ArraySchema<Yup.StringSchema>;
    keywordsToAdd: Yup.ArraySchema<Yup.StringSchema>;
  }>;
}> {
  return yup.object({
    input: yup.object({
      title: yup.string().required("Required"),
      script: yup.string().required("Required"),
      groupIds: yup
        .array()
        .of(yup.string().required())
        .min(1, "The script must be in at least one group."),
      keywordsToAdd: yup.array().of(
        yup
          .string()
          .required()
          .matches(
            /^([^\s]|[^\s].*[^\s])$/,
            "Keywords must not start or end with a space"
          )
          .test(
            "keyword_trim",
            "Keywords must not contain multiple spaces",
            value => !!value && !/\s{2,}/.test(value)
          )
          .max(
            MAX_KEYWORD_LENGTH,
            `Keywords must have no more than ${MAX_KEYWORD_LENGTH} characters`
          )
      ),
    }),
  });
}

export const tenantSlugRegex = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

export function createTenantInputSchema(yup: typeof Yup): Yup.ObjectSchema<{
  input: Yup.ObjectSchema<{ name: Yup.StringSchema; slug: Yup.StringSchema }>;
}> {
  return yup.object({
    input: yup.object({
      name: Yup.string().required("Required").min(1, "Too short"),
      slug: Yup.string()
        .required("Required")
        .min(2, "Too short")
        .max(32, "Too long")
        .matches(
          tenantSlugRegex,
          "Must be a valid subdomain (lowercase letters and hyphens; cannot start or end with a hyphen)"
        ),
      reservedPartition: Yup.number()
        .typeError("Must be an integer")
        .integer()
        .min(0)
        .max(63),
      orgOwnerEmail: Yup.string()
        .nullable()
        .when("orgOwnerName", {
          is: (orgOwnerName: string) => !!orgOwnerName,
          then: Yup.string().required("Required if name is provided"),
        }),
      orgOwnerName: Yup.string()
        .min(1, "Too short")
        .max(255, "Too long")
        .nullable(),
    }),
  });
}

export function editCampaignBasicsInputSchema(
  yup: typeof Yup
): Yup.ObjectSchema<{
  input: Yup.ObjectSchema<{
    name: Yup.StringSchema;
    description: Yup.StringSchema;
    inboxId: Yup.AnySchema;
    integrationId: Yup.AnySchema;
    integrationSettings: Yup.AnySchema;
    textingHoursTimezone: Yup.StringSchema;
    textingHoursStart: Yup.StringSchema;
    textingHoursEnd: Yup.StringSchema;
    texterAssignmentBatchSize: Yup.NumberSchema;
    texterAssignmentExpirationMinutes: Yup.NumberSchema;
  }>;
}> {
  return yup.object().shape({
    input: yup.object({
      name: yup.string().required("Name is required").min(3, "Too short"),
      description: yup.string(),
      inboxId: yup.string().uuid().nullable(),
      integrationId: yup.string().uuid().nullable(),
      integrationSettings: yup
        .object({
          databaseMode: yup.string().oneOf(["MY_CAMPAIGN", "MY_VOTERS"]),
        })
        .nullable(),
      textingHoursTimezone: yup.string(),
      textingHoursStart: startTime(),
      textingHoursEnd: endTime(),
      texterAssignmentBatchSize: yup.number(),
      texterAssignmentExpirationMinutes: yup.number(),
    }),
  });
}

export const tcrCampaignIdSchema = (
  yup: typeof Yup
): Yup.StringSchema<string | null | undefined> =>
  yup
    .string()
    .nullable()
    .matches(/^(C|c)(\w|\d){6}$/, {
      message:
        "Must be a valid TCR campaign ID (7-character string starting with C)",
    });

export const tcrBrandIdSchema = (
  yup: typeof Yup
): Yup.StringSchema<string | null | undefined> =>
  yup
    .string()
    .nullable()
    .matches(/^(B|b)(\w|\d){6}$/, {
      message:
        "Must be a valid TCR brand ID (7-character string starting with B)",
    });

export function createLinkShortenerDomainInputSchema(
  yup: typeof Yup
): Yup.ObjectSchema<{
  input: Yup.ObjectSchema<{
    domain: Yup.StringSchema;
    fallbackUrl: Yup.StringSchema;
  }>;
}> {
  return yup.object().shape({
    input: yup.object({
      domain: yup
        .string()
        .required("Required")
        .matches(DOMAIN_NAME_REGEX, "Must be a valid domain name"),
      fallbackUrl: yup.string().required("Required").url("Must be a valid URL"),
    }),
  });
}

const einRegex = /^\d{2}-?\d{7}$/;
const einRequiredSchema = Yup.string()
  .nullable()
  .test("invalid-ein-format", "Valid EIN required", value => {
    return value ? !!value.match(einRegex) : false;
  });
const einOptionalSchema = Yup.string()
  .nullable()
  .test("invalid-ein-format", "Valid EIN required", value => {
    return !value || !!value.match(einRegex);
  });

const uuidPattern = `[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}`;
const versionPattern = `\\d\\.\\d`;
// ie, cv|1.0|tcr|10dlc|UUID|RANDOM_case_SENSITIVE_string
const tenDlcCvTokenRegex = new RegExp(
  `cv\\|${versionPattern}\\|tcr\\|10dlc\\|${uuidPattern}\\|.+`
);
// ie, cv|1.0|mno|tfree|UUID|RANDOM_case_SENSITIVE_string
const tollFreeCvTokenRegex = new RegExp(
  `cv\\|${versionPattern}\\|mno\\|tfree\\|${uuidPattern}\\|.+`
);

export function updateLinkShortenerDomainInputSchema(
  yup: typeof Yup
): Yup.ObjectSchema<{
  input: Yup.ObjectSchema<{
    fallbackUrl: Yup.StringSchema;
  }>;
}> {
  return yup.object().shape({
    input: yup.object({
      fallbackUrl: yup.string().required("Required").url("Must be a valid URL"),
    }),
  });
}

export const tenDlcCvTokenSchema = (
  isRequired: boolean
): Yup.StringSchema<string | null | undefined> =>
  Yup.string()
    .transform(value => (value ? value : null))
    .nullable()
    .test(
      "no-toll-free-cv-token",
      "Toll-free CV tokens are not permitted",
      value => {
        return value ? !value.match(tollFreeCvTokenRegex) : true;
      }
    )
    .test(
      "no-uppercased-cv-tcr-10dlc",
      "'cv', 'tcr', and '10dlc' must be lowercased",
      value => {
        const segments: string = value
          ? value.split("|").slice(0, 4).join("|")
          : "";
        return !/[A-Z]/.test(segments);
      }
    )
    .test("invalid-cv-token-format", "Valid CV token required", value => {
      return value ? !!value.match(tenDlcCvTokenRegex) : !isRequired;
    });

export const tollFreeCvTokenSchema: Yup.StringSchema<
  string | null | undefined
> = Yup.string()
  .transform(value => (value ? value : null))
  .nullable()
  .test("no-10dlc-cv-token", "10DLC CV tokens are not permitted", value => {
    return value ? !value.match(tenDlcCvTokenRegex) : true;
  })
  .test(
    "no-uppercased-cv-mno-tfree",
    "'cv', 'mno', and 'tfree' must be lowercased",
    value => {
      const segments: string = value
        ? value.split("|").slice(0, 4).join("|")
        : "";
      return !/[A-Z]/.test(segments);
    }
  )
  .test("invalid-cv-token-format", "Valid CV token required", value => {
    return value ? !!value.match(tollFreeCvTokenRegex) : false;
  });

export const tollFreeCvTokenOptionalSchema: Yup.StringSchema<
  string | null | undefined
> = Yup.string()
  .transform(value => (value ? value : null))
  .nullable()
  .test("no-10dlc-cv-token", "10DLC CV tokens are not permitted", value => {
    return !value || !value.match(tenDlcCvTokenRegex);
  })
  .test(
    "no-uppercased-cv-mno-tfree",
    "'cv', 'mno', and 'tfree' must be lowercased",
    value => {
      if (!value) {
        return true;
      }
      const segments = value.split("|").slice(0, 4).join("|");
      return !/[A-Z]/.test(segments);
    }
  )
  .test("invalid-cv-token-format", "Valid CV token required", value => {
    return !value || !!value.match(tollFreeCvTokenRegex);
  });

const sampleScriptBase = (yup: typeof Yup, optOutPhrases: string[]) =>
  yup
    .string()
    .test(
      "hasOptOutPhrase",
      "Must include opt-out language such as 'STOP to opt-out'",
      s => includesOptOutLanguage(s, optOutPhrases)
    );

const validateUSPhoneNumber = (number: string | undefined): boolean => {
  if (!number) {
    return true;
  }

  const phoneUtil = PhoneNumberUtil.getInstance();
  try {
    const parsedNumber = phoneUtil.parse(number || "", "US");
    return phoneUtil.isValidNumber(parsedNumber);
  } catch {
    return false;
  }
};

const urlTransform = (value?: string | null): string | undefined | null =>
  !value ? value : value.startsWith("http") ? value : `https://${value}`;

export const tenDlcRegistrationSchema = (
  yup: typeof Yup,
  optOutPhrases: string[]
): Yup.ObjectSchema<{
  organizationName: Yup.StringSchema;
  displayName: Yup.StringSchema;
  orgType: Yup.StringSchema;
  ein: Yup.StringSchema;
  einIssuingCountry: Yup.StringSchema;
  cvToken: Yup.StringSchema;
  focusArea: Yup.StringSchema;
  expectedTexts: Yup.StringSchema<string | null | undefined>;
  desiredAreaCodes: Yup.ArraySchema<Yup.StringSchema>;
  addressOne: Yup.StringSchema;
  addressTwo: Yup.StringSchema;
  city: Yup.StringSchema;
  state: Yup.StringSchema;
  postalCode: Yup.StringSchema;
  country: Yup.StringSchema;
  organizationContactName: Yup.StringSchema;
  organizationContactPhone: Yup.StringSchema;
  organizationContactEmail: Yup.StringSchema;
  orgContactIsPrimaryContact: Yup.BooleanSchema;
  primaryContactName: Yup.StringSchema<string | null | undefined>;
  primaryContactPhone: Yup.StringSchema<string | null | undefined>;
  primaryContactEmail: Yup.StringSchema<string | null | undefined>;
  website: Yup.StringSchema;
  useCase: Yup.StringSchema<string | null | undefined>;
  description: Yup.StringSchema;
  embeddedPhone: Yup.BooleanSchema;
  optoutMessage: Yup.StringSchema;
  helpMessage: Yup.StringSchema;
  termsAndConditionsLink: Yup.StringSchema;
  optInConfirmationMessage: Yup.StringSchema;
  createComplianceBundle: Yup.BooleanSchema;
  optInLink: Yup.StringSchema;
  privacyPolicyLink: Yup.StringSchema;
  sampleScript1: Yup.StringSchema<string | null | undefined>;
  sampleScript2: Yup.StringSchema<string | null | undefined>;
  sampleScript3: Yup.StringSchema<string | null | undefined>;
  sampleScript4: Yup.StringSchema<string | null | undefined>;
  additionalContext: Yup.StringSchema;
  usageTypes: Yup.ArraySchema<Yup.StringSchema>;
  submitToStaff: Yup.BooleanSchema;
}> => {
  return yup.object().shape({
    organizationName: yup
      .string()
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("Organization name is required"),
      })
      .min(3, "Too short"),
    orgType: yup
      .string()
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("Organization type is required"),
      })
      .oneOf(["C3", "C4", "C5", "POLITICAL", "PRIVATE", "GOVERNMENT"]),
    displayName: yup.string().when("submitToStaff", {
      is: true,
      then: yup.string().required("DBA is required"),
    }),
    ein: yup
      .string()
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("EIN is required"),
      })
      .matches(einRegex, "Invalid EIN"),
    einIssuingCountry: yup
      .string()
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("Issuing country is required"),
      })
      .length(2, "Invalid country code"),
    cvToken: yup.string().when("orgType", {
      is: "POLITICAL",
      then: yup.string().when("submitToStaff", {
        is: true,
        then: tenDlcCvTokenSchema(true),
      }),
      otherwise: yup.string().optional().nullable(),
    }),
    focusArea: yup.string().when("orgType", {
      is: "PRIVATE",
      then: yup.string().when("submitToStaff", {
        is: true,
        then: yup.string().required("Focus area is required"),
      }),
      otherwise: yup.string().optional(),
    }),
    expectedTexts: yup
      .string()
      .oneOf([
        "TEN_K_OR_FEWER",
        "ONE_HUNDRED_K_OR_FEWER",
        "FIVE_HUNDRED_K_OR_FEWER",
        "ONE_MILLION_OR_FEWER",
        "ONE_MILLION_OR_MORE",
      ])
      .nullable()
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("Expected texts is required"),
      }),
    desiredAreaCodes: yup
      .array()
      .of(yup.string().matches(/^\d{3}$/, "Invalid area code"))
      .when("submitToStaff", {
        is: true,
        then: yup
          .array()
          .min(1, "At least one area code is required")
          .required("At least one area code is required"),
      }),
    preferredAreaCode: yup
      .string()
      .nullable()
      .matches(/^\d{3}$/, "Invalid area code")
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("Preferred area code is required"),
      }),
    addressOne: yup.string().when("submitToStaff", {
      is: true,
      then: yup.string().required("Address is required"),
    }),
    addressTwo: yup.string().optional(),
    city: yup.string().when("submitToStaff", {
      is: true,
      then: yup.string().required("City is required"),
    }),
    state: yup
      .string()
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("State is required"),
      })
      .length(2, "Invalid state abbreviation"),
    postalCode: yup
      .string()
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("Postal code is required"),
      })
      .matches(/^\d{5}([ -]\d{4})?$/, "Invalid postal code"),
    country: yup
      .string()
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("Country is required"),
      })
      .length(2, "Invalid country code"),
    organizationContactName: yup.string().when("submitToStaff", {
      is: true,
      then: yup.string().required("Organization contact name is required"),
    }),
    organizationContactPhone: yup
      .string()
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("Organization contact phone is required"),
      })
      .test(
        "valid-number",
        "Must be valid US phone number",
        validateUSPhoneNumber
      ),
    organizationContactEmail: yup
      .string()
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("Organization contact email is required"),
      })
      .email("Must be valid email address"),
    orgContactIsPrimaryContact: yup.bool().optional(),
    primaryContactName: yup.string().when("orgContactIsPrimaryContact", {
      is: false,
      then: Yup.string().required("Contact name is required"),
      otherwise: Yup.string().optional(),
    }),
    primaryContactPhone: yup
      .string()
      .when("orgContactIsPrimaryContact", {
        is: false,
        then: yup.string().required("Contact phone is required"),
        otherwise: yup.string().optional(),
      })
      .test(
        "valid-number",
        "Must be valid US phone number",
        validateUSPhoneNumber
      ),
    primaryContactEmail: yup
      .string()
      .email("Must be valid email")
      .when("orgContactIsPrimaryContact", {
        is: false,
        then: yup.string().required("Contact email is required"),
        otherwise: yup.string().optional(),
      }),
    website: yup
      .string()
      .max(255, "Website must be at most 255 characters")
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("Website is required"),
      })
      .transform(urlTransform)
      .url("Invalid URL"),

    useCase: yup.string().optional().nullable(),
    description: yup
      .string()
      .min(40, "Description must be at least 40 characters")
      .max(4096, "Description must be at most 4096 characters")
      .when("submitToStaff", {
        is: true,
        then: yup.string().required("Description is required"),
      }),
    embeddedPhone: yup.bool().when("submitToStaff", {
      is: true,
      then: yup.bool().required("Embedded phone is required"),
    }),
    optoutMessage: yup.string().optional(),
    helpMessage: yup.string().optional(),
    optInConfirmationMessage: yup.string().optional(),

    acknowledgedCompliance: yup.bool().when("submitToStaff", {
      is: true,
      then: yup
        .bool()
        .test(
          "is-acknowledged",
          "Acknowledgement of compliance requirements is required",
          value => !!value
        )
        .required("Acknowledgement of compliance requirements is required"),
    }),
    createComplianceBundle: yup.bool().when("submitToStaff", {
      is: true,
      then: yup.bool().required("Create opt in form is required"),
    }),

    termsAndConditionsLink: yup
      .string()
      .max(255)
      .when("submitToStaff", {
        is: true,
        then: yup.string().when("createComplianceBundle", {
          is: false,
          then: yup
            .string()
            .required("Terms and conditions link is required")
            .transform(urlTransform)
            .url("Invalid URL"),
        }),
      }),
    optInLink: yup
      .string()
      .max(255)
      .when("submitToStaff", {
        is: true,
        then: yup.string().when("createComplianceBundle", {
          is: false,
          then: yup
            .string()
            .required("Opt-in link is required")
            .transform(urlTransform)
            .url("Invalid URL"),
        }),
      }),
    privacyPolicyLink: yup
      .string()
      .max(255)
      .when("submitToStaff", {
        is: true,
        then: yup.string().when("createComplianceBundle", {
          is: false,
          then: yup
            .string()
            .required("Privacy policy link is required")
            .transform(urlTransform)
            .url("Invalid URL"),
        }),
      }),

    sampleScript1: sampleScriptBase(yup, optOutPhrases).when("submitToStaff", {
      is: true,
      then: yup
        .string()
        .required("Two sample scripts are required")
        .min(20, "Must be at least 20 characters")
        .max(1024, "Must be at most 1024 characters"),
    }),
    sampleScript2: sampleScriptBase(yup, optOutPhrases).when("submitToStaff", {
      is: true,
      then: yup
        .string()
        .required("Two sample scripts are required")
        .min(20, "Must be at least 20 characters")
        .max(1024, "Must be at most 1024 characters"),
    }),
    sampleScript3: sampleScriptBase(yup, optOutPhrases).when("submitToStaff", {
      is: true,
      then: yup
        .string()
        .nullable()
        .optional()
        .min(20, "Must be at least 20 characters")
        .max(1024, "Must be at most 1024 characters"),
    }),
    sampleScript4: sampleScriptBase(yup, optOutPhrases).when("submitToStaff", {
      is: true,
      then: yup
        .string()
        .nullable()
        .optional()
        .min(20, "Must be at least 20 characters")
        .max(1024, "Must be at most 1024 characters"),
    }),

    additionalContext: yup.string().optional(),
    usageTypes: yup
      .array()
      .of(yup.string().oneOf(["DIALER", "TEXTER"]))
      .when("submitToStaff", {
        is: true,
        then: yup
          .array()
          .min(1, "Please select at least one")
          .required("Please select at least one"),
        otherwise: yup.array().optional(),
      }),

    submitToStaff: yup.bool(),
  });
};

function includesOptOutLanguage(
  s: string | undefined,
  optOutPhrases: string[]
): boolean {
  if (!s) {
    return true;
  }

  for (const phrase of optOutPhrases) {
    if (s.toLowerCase().includes(phrase.toLowerCase().trim())) {
      return true;
    }
  }

  return false;
}

export const tenDlcStaffCampaignRegistrationSchema = (
  yup: typeof Yup,
  optOutPhrases: string[]
): Yup.ObjectSchema<{
  useCase: Yup.StringSchema;
  description: Yup.StringSchema;
  embeddedPhone: Yup.BooleanSchema;
  messageFlowStaff: Yup.StringSchema;
  optoutMessage: Yup.StringSchema;
  helpMessage: Yup.StringSchema;
  termsAndConditionsLink: Yup.StringSchema;
  optInConfirmationMessage: Yup.StringSchema;
  createComplianceBundle: Yup.BooleanSchema;
  optInLink: Yup.StringSchema;
  privacyPolicyLink: Yup.StringSchema;
  sampleScript1: Yup.StringSchema<string | null | undefined>;
  sampleScript2: Yup.StringSchema<string | null | undefined>;
  sampleScript3: Yup.StringSchema<string | null | undefined>;
  sampleScript4: Yup.StringSchema<string | null | undefined>;
  additionalContext: Yup.StringSchema;
  usageTypes: Yup.ArraySchema<Yup.StringSchema>;
  expediteStatus: Yup.StringSchema;
  validateRequiredFields: Yup.BooleanSchema;
}> => {
  return yup.object().shape({
    validateRequiredFields: yup.bool().default(false).required(),
    useCase: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .required("Use case is required")
        .oneOf([
          "CHARITY",
          "POLITICAL",
          "HIGHER_EDUCATION",
          "PUBLIC_SERVICE_ANNOUNCEMENT",
          "POLLING_VOTING",
          "ACCOUNT_NOTIFICATION",
          "CUSTOMER_CARE",
        ]),
    }),
    description: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .min(40, "Description must be at least 40 characters")
        .max(4096, "Description must be at most 4096 characters")
        .required("Description is required"),
    }),
    embeddedPhone: yup.bool().when("validateRequiredFields", {
      is: true,
      then: yup.bool().required("Embedded phone is required"),
    }),
    messageFlowStaff: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .min(40, "Message flow must be at least 40 characters")
        .max(4096, "Message flow must be at most 4096 characters")
        .required("Message flow is required"),
    }),
    optoutMessage: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .min(20, "Opt-out message must be at least 20 characters")
        .max(1024, "Opt-out message must be at most 1024 characters")
        .required("Opt-out message is required"),
    }),
    helpMessage: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .min(20, "Help message must be at least 20 characters")
        .max(1024, "Help message must be at most 1024 characters")
        .required("Help message is required"),
    }),
    termsAndConditionsLink: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .max(255, "Terms and conditions link must be at most 255 characters")
        .required("Terms and conditions link is required")
        .transform(urlTransform)
        .url("Invalid URL"),
    }),
    optInConfirmationMessage: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .required("Opt-in confirmation message is required")
        .min(20, "Opt-in confirmation message must be at least 20 characters")
        .max(
          1024,
          "Opt-in confirmation message must be at most 1024 characters"
        ),
    }),
    acknowledgedCompliance: yup.bool().when("validateRequiredFields", {
      is: true,
      then: yup
        .bool()
        .required("Acknowledgement of compliance requirements is required"),
    }),
    createComplianceBundle: yup.bool().when("validateRequiredFields", {
      is: true,
      then: yup.bool().required("Create opt in form is required"),
    }),
    optInLink: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .max(255, "Opt-in link must be at most 255 characters")
        .required("Opt-in link is required")
        .transform(urlTransform)
        .url("Invalid URL"),
    }),
    privacyPolicyLink: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .max(255, "Privacy policy link must be at most 255 characters")
        .required("Privacy policy link is required")
        .transform(urlTransform)
        .url("Invalid URL"),
    }),

    sampleScript1: yup.string().when("validateRequiredFields", {
      is: true,
      then: sampleScriptBase(yup, optOutPhrases)
        .nullable(false)
        .required("Two sample scripts are required"),
    }),
    sampleScript2: yup.string().when("validateRequiredFields", {
      is: true,
      then: sampleScriptBase(yup, optOutPhrases)
        .nullable(false)
        .required("Two sample scripts are required"),
    }),
    sampleScript3: yup.string().when("validateRequiredFields", {
      is: true,
      then: sampleScriptBase(yup, optOutPhrases),
    }),
    sampleScript4: yup.string().when("validateRequiredFields", {
      is: true,
      then: sampleScriptBase(yup, optOutPhrases),
    }),
    providerName: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .required("Please select a provider")
        .oneOf(["BANDWIDTH", "SINCH"]),
    }),

    additionalContext: yup.string().optional(),
    usageTypes: yup
      .array()
      .of(yup.string().oneOf(["DIALER", "TEXTER"]))
      .when("validateRequiredFields", {
        is: true,
        then: yup
          .array()
          .min(1, "Please select at least one usage type")
          .required("Please select at least one usage type"),
        otherwise: yup.array().optional(),
      }),
    expediteStatus: yup.string().oneOf(["NONE", "REQUESTED", "PAID"]),
  });
};

export const tenDlcStaffBrandRegistrationSchema = (
  yup: typeof Yup
): Yup.ObjectSchema<{
  tenantId: Yup.StringSchema;
  subaccountId: Yup.StringSchema;
  organizationName: Yup.StringSchema;
  displayName: Yup.StringSchema;
  orgType: Yup.StringSchema;
  ein: Yup.StringSchema;
  einIssuingCountry: Yup.StringSchema;
  cvToken: Yup.StringSchema;
  focusArea: Yup.StringSchema;
  expectedTexts: Yup.StringSchema<string | null | undefined>;
  desiredAreaCodes: Yup.ArraySchema<Yup.StringSchema>;
  addressOne: Yup.StringSchema;
  addressTwo: Yup.StringSchema;

  city: Yup.StringSchema;
  state: Yup.StringSchema;
  postalCode: Yup.StringSchema;
  country: Yup.StringSchema;
  organizationContactName: Yup.StringSchema;
  organizationContactPhone: Yup.StringSchema;
  organizationContactEmail: Yup.StringSchema;
  primaryContactName: Yup.StringSchema<string | null | undefined>;
  primaryContactPhone: Yup.StringSchema<string | null | undefined>;
  primaryContactEmail: Yup.StringSchema<string | null | undefined>;
  website: Yup.StringSchema;
  validateRequiredFields: Yup.BooleanSchema;
}> => {
  return yup.object().shape({
    validateRequiredFields: yup.bool().default(false).required(),
    tenantId: yup.string().required("Tenant ID is required"),
    subaccountId: yup.string().required("Subaccount ID is required"),
    organizationName: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .required("Organization name is required")
        .min(3, "Too short"),
    }),
    displayName: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup.string().required("DBA is required"),
    }),
    orgType: yup
      .string()
      .oneOf(["C3", "C4", "C5", "POLITICAL", "PRIVATE", "GOVERNMENT"]),
    ein: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .required("EIN is required")
        .matches(einRegex, "Invalid EIN"),
    }),
    einIssuingCountry: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .required("Issuing country is required")
        .length(2, "Invalid country code"),
    }),
    cvToken: yup.string().when("orgType", {
      is: "POLITICAL",
      then: yup.string().when("validateRequiredFields", {
        is: true,
        then: tenDlcCvTokenSchema(true),
        otherwise: yup.string(),
      }),
      otherwise: yup.string().optional(),
    }),
    focusArea: yup.string().when("orgType", {
      is: "PRIVATE",
      then: yup.string().when("validateRequiredFields", {
        is: true,
        then: yup.string().required("Focus area is required"),
        otherwise: yup.string(),
      }),
      otherwise: yup.string().nullable(),
    }),
    expectedTexts: yup
      .string()
      .oneOf([
        "TEN_K_OR_FEWER",
        "ONE_HUNDRED_K_OR_FEWER",
        "FIVE_HUNDRED_K_OR_FEWER",
        "ONE_MILLION_OR_FEWER",
        "ONE_MILLION_OR_MORE",
      ])
      .when("validateRequiredFields", {
        is: true,
        then: yup.string().required("Expected texts is required"),
      }),
    desiredAreaCodes: yup
      .array()
      .of(yup.string().matches(/^\d{3}$/, "Invalid area code"))
      .when("validateRequiredFields", {
        is: true,
        then: yup.array().min(1, "At least one area code is required"),
      }),
    preferredAreaCode: yup
      .string()
      .nullable()
      .matches(/^\d{3}$/, "Invalid area code")
      .when("validateRequiredFields", {
        is: true,
        then: yup.string().required("Preferred area code is required"),
      }),
    addressOne: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup.string().required("Address is required"),
    }),
    addressTwo: yup.string().optional(),
    city: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup.string().required("City is required"),
    }),
    state: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .required("State is required")
        .length(2, "Invalid state abbreviation"),
    }),
    postalCode: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .required("Postal code is required")
        .matches(/^\d{5}([ -]\d{4})?$/, "Invalid postal code"),
    }),
    country: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .required("Country is required")
        .length(2, "Invalid country code"),
    }),
    organizationContactName: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup.string().required("Organization contact name is required"),
    }),
    organizationContactPhone: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup.string().required("Organization contact phone is required"),
    }),
    organizationContactEmail: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .required("Organization contact email is required")
        .email("Invalid email"),
    }),
    primaryContactName: yup.string().optional().nullable(),
    primaryContactPhone: yup.string().optional().nullable(),
    primaryContactEmail: yup.string().optional().nullable(),
    website: yup.string().when("validateRequiredFields", {
      is: true,
      then: yup
        .string()
        .required("Website is required")
        .max(255)
        .transform(urlTransform)
        .url("Invalid URL"),
      otherwise: yup
        .string()
        .max(255)
        .transform(urlTransform)
        .url("Invalid URL"),
    }),
  });
};

type BaseTollFreeRegistrationSchema = {
  organizationName: Yup.StringSchema;
  orgType: Yup.StringSchema<string | null | undefined>;
  displayName: Yup.StringSchema<string | null | undefined>;
  ein: Yup.StringSchema<string | null | undefined>;
  cvToken: Yup.StringSchema<string | null | undefined>;
  address1: Yup.StringSchema;
  address2: Yup.StringSchema<string | null | undefined>;
  city: Yup.StringSchema;
  state: Yup.StringSchema;
  postalCode: Yup.StringSchema;
  country: Yup.StringSchema;
  website: Yup.StringSchema;
  contactFirstName: Yup.StringSchema;
  contactLastName: Yup.StringSchema;
  contactEmail: Yup.StringSchema;
  contactPhone: Yup.StringSchema;
  orgContactIsPrimaryContact: Yup.BooleanSchema;
  primaryContactFirstName: Yup.StringSchema<string | null | undefined>;
  primaryContactLastName: Yup.StringSchema<string | null | undefined>;
  primaryContactEmail: Yup.StringSchema<string | null | undefined>;
  primaryContactPhone: Yup.StringSchema<string | null | undefined>;
  messageVolume: Yup.NumberSchema;
  useCaseSummary: Yup.StringSchema;
  productionMessageContent: Yup.StringSchema;
  usageTypes: Yup.ArraySchema<Yup.StringSchema>;
  optInPageUrl: Yup.StringSchema<string | null | undefined>;
  privacyPolicyUrl: Yup.StringSchema<string | null | undefined>;
  notes: Yup.StringSchema<string | null | undefined>;
  termsAndConditionsUrl: Yup.StringSchema<string | null | undefined>;
  inviteTokenId: Yup.StringSchema<string | null | undefined>;
};

export const baseTollFreeRegistrationSchema = (
  optOutPhrases: string[],
  validateRequiredFields = true
): Yup.ObjectSchema<BaseTollFreeRegistrationSchema> =>
  Yup.object().shape({
    organizationName: validateRequiredFields
      ? Yup.string()
          .max(500, "Organization name must be at most 500 characters")
          .required("Organization name is required")
      : Yup.string().max(
          500,
          "Organization name must be at most 500 characters"
        ),
    orgType: validateRequiredFields
      ? Yup.string()
          .oneOf([
            "C3",
            "C4",
            "C5",
            "POLITICAL",
            "GOVERNMENT",
            "PRIVATE",
            "OTHER",
          ])
          .required("Organization type is required")
      : Yup.string()
          .nullable()
          .transform(v => v || null)
          .oneOf([
            "C3",
            "C4",
            "C5",
            "POLITICAL",
            "GOVERNMENT",
            "PRIVATE",
            "OTHER",
            null,
          ]),
    displayName: validateRequiredFields
      ? Yup.string().max(500).required("DBA is required")
      : Yup.string().max(500),
    ein: validateRequiredFields ? einRequiredSchema : einOptionalSchema,
    cvToken: Yup.string().when("orgType", {
      is: "POLITICAL",
      then: validateRequiredFields
        ? tollFreeCvTokenSchema
        : tollFreeCvTokenOptionalSchema,
      otherwise: Yup.string().optional().nullable(),
    }),
    address1: validateRequiredFields
      ? Yup.string().max(500).required("Address is required")
      : Yup.string().max(500),
    address2: Yup.string().max(500).nullable(),
    city: validateRequiredFields
      ? Yup.string().max(500).required("City is required")
      : Yup.string().max(500),
    state: validateRequiredFields
      ? Yup.string().max(500).required("State is required")
      : Yup.string().max(500),
    postalCode: validateRequiredFields
      ? Yup.string().max(500).required("Postal code is required")
      : Yup.string().max(500),
    country: validateRequiredFields
      ? Yup.string().max(500).required("Country is required")
      : Yup.string().max(500),
    website: validateRequiredFields
      ? Yup.string()
          .max(500)
          .transform(urlTransform)
          .url("Invalid URL")
          .required("Website is required")
      : Yup.string().max(500).transform(urlTransform).url("Invalid URL"),
    contactFirstName: validateRequiredFields
      ? Yup.string().max(500).required("First name is required")
      : Yup.string().max(500),
    contactLastName: validateRequiredFields
      ? Yup.string().max(500).required("Last name is required")
      : Yup.string().max(500),
    contactEmail: validateRequiredFields
      ? Yup.string().max(500).required("Email is required")
      : Yup.string().max(500),
    contactPhone: validateRequiredFields
      ? Yup.string().max(500).required("Phone is required")
      : Yup.string().max(500),

    orgContactIsPrimaryContact: validateRequiredFields
      ? Yup.boolean().required()
      : Yup.boolean(),

    primaryContactFirstName: validateRequiredFields
      ? Yup.string()
          .max(500)
          .when("orgContactIsPrimaryContact", {
            is: false,
            then: Yup.string().required("Contact first name is required"),
            otherwise: Yup.string().nullable(),
          })
      : Yup.string().max(500).nullable(),
    primaryContactLastName: validateRequiredFields
      ? Yup.string()
          .max(500)
          .when("orgContactIsPrimaryContact", {
            is: false,
            then: Yup.string().required("Contact last name is required"),
            otherwise: Yup.string().nullable(),
          })
      : Yup.string().max(500).nullable(),
    primaryContactEmail: validateRequiredFields
      ? Yup.string()
          .max(500)
          .email("Invalid email")
          .when("orgContactIsPrimaryContact", {
            is: false,
            then: Yup.string().required("Contact email is required"),
            otherwise: Yup.string().nullable(),
          })
      : Yup.string().max(500).email("Invalid email").nullable(),
    primaryContactPhone: validateRequiredFields
      ? Yup.string()
          .max(500)
          .when("orgContactIsPrimaryContact", {
            is: false,
            then: Yup.string().required("Contact phone is required"),
            otherwise: Yup.string().nullable(),
          })
      : Yup.string().max(500).nullable(),

    messageVolume: validateRequiredFields
      ? Yup.number()
          .min(10, "The minimum texts per month is 10")
          .max(10000000, "The maximum texts per month is 10 million")
          .required("Expected texts per month is required")
      : Yup.number()
          .min(10, "The minimum texts per month is 10")
          .max(10000000, "The maximum texts per month is 10 million"),

    useCaseSummary: validateRequiredFields
      ? Yup.string()
          .max(500, "Must be at most 500 characters")
          .required("Description is required")
      : Yup.string().max(500, "Must be at most 500 characters"),

    productionMessageContent: validateRequiredFields
      ? Yup.string()
          .max(500, "Must be at most 500 characters")
          .test(
            "hasOptOutPhrase",
            "Must include opt out language such as 'STOP to opt out'",
            s => includesOptOutLanguage(s, optOutPhrases)
          )
          .required("Must provide sample messages")
      : Yup.string()
          .max(500, "Must be at most 500 characters")
          .test(
            "hasOptOutPhrase",
            "Must include opt out language such as 'STOP to opt out'",
            s => includesOptOutLanguage(s, optOutPhrases)
          ),

    usageTypes: validateRequiredFields
      ? Yup.array()
          .of(Yup.string())
          .min(1, "Please select at least one usage type")
          .required("Please select at least one usage type")
      : Yup.array().of(Yup.string()),

    optInPageUrl: validateRequiredFields
      ? Yup.string()
          .max(500)
          .when("createComplianceBundle", {
            is: false,
            then: Yup.string().required("Opt in page URL is required"),
            otherwise: Yup.string().nullable(),
          })
      : Yup.string().max(500).nullable(),

    privacyPolicyUrl: validateRequiredFields
      ? Yup.string()
          .max(500)
          .when("createComplianceBundle", {
            is: false,
            then: Yup.string().required("Privacy policy URL is required"),
            otherwise: Yup.string().nullable(),
          })
      : Yup.string().max(500).nullable(),

    inviteTokenId: Yup.string().nullable(),
    notes: Yup.string().max(2000).nullable(),
    termsAndConditionsUrl: Yup.string().max(500).nullable(),
  });

export const tollFreeRegistrationSchema = (
  optOutPhrases: string[],
  validateRequiredFields = true
): Yup.ObjectSchema<
  BaseTollFreeRegistrationSchema & {
    createComplianceBundle: Yup.BooleanSchema;
    acknowledgedCompliance: Yup.BooleanSchema;
  }
> =>
  baseTollFreeRegistrationSchema(optOutPhrases, validateRequiredFields).shape({
    createComplianceBundle: validateRequiredFields
      ? Yup.boolean().required()
      : Yup.boolean(),
    acknowledgedCompliance: validateRequiredFields
      ? Yup.boolean()
          .oneOf([true], "Must acknowledge compliance")
          .required("Must acknowledge compliance")
      : Yup.boolean(),
  });

export const staffTollFreeRegistrationSchema = (
  optOutPhrases: string[]
): Yup.ObjectSchema<
  BaseTollFreeRegistrationSchema & {
    createComplianceBundle: Yup.BooleanSchema;
    useCase: Yup.StringSchema<string | null | undefined>;
    optInWorkflowDescription: Yup.StringSchema<string | null | undefined>;
    optInWorkflowImageUrls: Yup.ArraySchema<Yup.StringSchema>;
    requestedTnCount: Yup.NumberSchema<number | null | undefined>;
    additionalContext: Yup.StringSchema<string | null | undefined>;
  }
> =>
  baseTollFreeRegistrationSchema(optOutPhrases).shape({
    createComplianceBundle: Yup.boolean().required(),
    useCase: Yup.string().max(500).nullable(),
    optInWorkflowDescription: Yup.string().max(500).nullable(),
    optInWorkflowImageUrls: Yup.array().of(Yup.string().max(500)),
    requestedTnCount: Yup.number()
      .min(1, "Number of toll-free numbers to purchase must be at least 1")
      .max(5, "Cannot purchase more than 5 numbers")
      .required("Number of toll-free numbers to purchase is required"),
    additionalContext: Yup.string().nullable(),
  });

export const staffTollFreeRegistrationForBandwidthSchema = (
  optOutPhrases: string[]
): Yup.ObjectSchema<
  BaseTollFreeRegistrationSchema & {
    createComplianceBundle: Yup.BooleanSchema;
    acknowledgedCompliance: Yup.BooleanSchema;
    subaccountId: Yup.StringSchema;
    useCase: Yup.StringSchema<string | null | undefined>;
    optInWorkflowDescription: Yup.StringSchema<string | null | undefined>;
    optInWorkflowImageUrls: Yup.ArraySchema<Yup.StringSchema>;
    additionalContext: Yup.StringSchema<string | null | undefined>;
  }
> =>
  tollFreeRegistrationSchema(optOutPhrases).shape({
    subaccountId: Yup.string().required("Subaccount ID is required"),
    useCase: Yup.string().max(500).required("Must provide a use case"),
    optInWorkflowDescription: Yup.string()
      .max(500)
      .required("Must provide an opt in workflow description"),
    optInWorkflowImageUrls: Yup.array()
      .of(Yup.string().max(500))
      .min(1, "Must provide at least one opt in workflow image URL"),
    additionalContext: Yup.string().nullable(),
  });

export type StaffTollFreeRegistrationForBandwidth = Yup.InferType<
  ReturnType<typeof staffTollFreeRegistrationForBandwidthSchema>
>;
